import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, Search, RefreshCw, Loader2, Edit, Trash2, Copy, Eye, X,
  FileText, Globe, Check, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminCmsPage = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    url_path: '',
    lang: 'en',
    status: 'draft',
    page_type: 'page', // 'page' or 'blog'
    seo_title: '',
    meta_description: '',
    canonical_url: '',
    robots_meta: 'index, follow',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
    content_html: '',
    json_ld: '',
    show_in_footer: false,
    footer_label: '',
    footer_group: '',
    footer_order: 99,
    // Blog specific fields
    blog_cover_image: '',
    blog_excerpt: '',
    blog_author: 'GoTurkeyTransfer',
    blog_category: '',
    blog_tags: '',
    blog_read_time: ''
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchPages = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '100'); // Get all pages, not just 20

      const response = await fetch(`${API_BASE}/api/cms/admin/pages?${params}`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error('Fetch pages error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageDetail = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/api/cms/admin/pages/${id}`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success && data.page) {
        setEditingPage(data.page);
        setFormData({
          title: data.page.title || '',
          url_path: data.page.url_path || '',
          lang: data.page.lang || 'en',
          status: data.page.status || 'draft',
          page_type: data.page.page_type || 'page',
          seo_title: data.page.seo_title || '',
          meta_description: data.page.meta_description || '',
          canonical_url: data.page.canonical_url || '',
          robots_meta: data.page.robots_meta || 'index, follow',
          og_title: data.page.og_title || '',
          og_description: data.page.og_description || '',
          og_image: data.page.og_image || '',
          twitter_title: data.page.twitter_title || '',
          twitter_description: data.page.twitter_description || '',
          twitter_image: data.page.twitter_image || '',
          content_html: data.page.content_html || data.page.html_content || '',
          json_ld: data.page.json_ld || '',
          show_in_footer: data.page.show_in_footer || false,
          footer_label: data.page.footer_label || '',
          footer_group: data.page.footer_group || '',
          footer_order: data.page.footer_order || 99,
          // Blog specific fields
          blog_cover_image: data.page.blog_cover_image || '',
          blog_excerpt: data.page.blog_excerpt || '',
          blog_author: data.page.blog_author || 'GoTurkeyTransfer',
          blog_category: data.page.blog_category || '',
          blog_tags: Array.isArray(data.page.blog_tags) ? data.page.blog_tags.join(', ') : (data.page.blog_tags || ''),
          blog_read_time: data.page.blog_read_time || ''
        });
        setShowEditor(true);
      }
    } catch (error) {
      console.error('Fetch page detail error:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.url_path) {
      toast.error('URL path is required');
      return;
    }

    setIsSaving(true);
    try {
      const url = editingPage 
        ? `${API_BASE}/api/cms/admin/pages/${editingPage.sqlite_id}`
        : `${API_BASE}/api/cms/admin/pages`;
      
      const method = editingPage ? 'PUT' : 'POST';

      // Prepare data for submission
      const submitData = {
        ...formData,
        // Convert blog_tags from comma-separated string to array
        blog_tags: formData.blog_tags 
          ? formData.blog_tags.split(',').map(t => t.trim()).filter(t => t)
          : []
      };

      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingPage ? 'Page updated' : 'Page created');
        setShowEditor(false);
        setEditingPage(null);
        resetForm();
        fetchPages();
      } else {
        toast.error(data.detail || data.error || 'Error occurred');
      }
    } catch (error) {
      console.error('Save page error:', error);
      toast.error('Save error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/cms/admin/pages/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Page deleted');
        fetchPages();
      }
    } catch (error) {
      console.error('Delete page error:', error);
      toast.error('Delete error');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/api/cms/admin/pages/${id}/duplicate`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Page duplicated');
        fetchPages();
      }
    } catch (error) {
      console.error('Duplicate page error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url_path: '',
      lang: 'en',
      status: 'draft',
      page_type: 'page',
      seo_title: '',
      meta_description: '',
      canonical_url: '',
      robots_meta: 'index, follow',
      og_title: '',
      og_description: '',
      og_image: '',
      twitter_title: '',
      twitter_description: '',
      twitter_image: '',
      content_html: '',
      json_ld: '',
      show_in_footer: false,
      footer_label: '',
      footer_group: '',
      footer_order: 99,
      blog_cover_image: '',
      blog_excerpt: '',
      blog_author: 'GoTurkeyTransfer',
      blog_category: '',
      blog_tags: '',
      blog_read_time: ''
    });
  };

  const handleNewPage = () => {
    setEditingPage(null);
    resetForm();
    setShowEditor(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchPages();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-500/20 text-green-400 border-green-500/30',
      draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    const labels = {
      published: 'Published',
      draft: 'Draft',
      archived: 'Archived'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded border ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="cms-title">CMS Pages</h1>
              <p className="text-muted-foreground">{pages.length} pages</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchPages} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Link to="/admin/pages/new">
                <Button data-testid="new-page-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Page
                </Button>
              </Link>
            </div>
          </div>

          {/* Search */}
          <Card className="bg-secondary border-border bg-secondary border-border mb-6">
            <CardContent className="p-4">
              <form onSubmit={(e) => { e.preventDefault(); fetchPages(); }} className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by URL or title..."
                    className="pl-10 bg-muted/30 border-border"
                    data-testid="cms-search-input"
                  />
                </div>
                <Button type="submit">Search</Button>
              </form>
            </CardContent>
          </Card>

          {/* Pages List */}
          <Card className="bg-secondary border-border bg-secondary border-border">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : pages.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No pages found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">URL</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Title</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Lang</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Updated</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map((page) => (
                        <tr key={page.sqlite_id} className="border-b border-border hover:bg-muted/20" data-testid={`cms-page-${page.sqlite_id}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="font-mono text-sm text-foreground">{page.url_path}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-foreground">{page.seo_title?.substring(0, 40) || page.title?.substring(0, 40) || '-'}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground uppercase">{page.lang}</span>
                          </td>
                          <td className="p-4">{getStatusBadge(page.status)}</td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground">
                              {page.updated_at?.split('T')[0] || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <Link
                                to={`/admin/pages/edit/${page.sqlite_id}`}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted/50 transition-colors"
                                title="Edit in Advanced Editor"
                              >
                                <Edit className="h-4 w-4 text-foreground" />
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchPageDetail(page.sqlite_id)}
                                title="Quick Edit"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicate(page.sqlite_id)}
                                title="Duplicate"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const url = page.url_path.startsWith('/') ? page.url_path : `/${page.url_path}`;
                                  window.open(url, '_blank');
                                }}
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(page.sqlite_id)}
                                title="Delete"
                                className="text-red-500 hover:text-red-400"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Page Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-secondary border-border bg-secondary border-border w-full max-w-4xl max-h-[90vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-secondary z-10">
              <CardTitle className="text-lg text-foreground">
                {editingPage ? 'Edit Page' : 'New Page'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-foreground">URL Path *</Label>
                  <Input
                    value={formData.url_path}
                    onChange={(e) => setFormData({...formData, url_path: e.target.value})}
                    placeholder="/about-us/"
                    className="bg-muted/30 border-border mt-1"
                    data-testid="url-path-input"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <Label className="text-foreground">Page Type</Label>
                  <select
                    value={formData.page_type}
                    onChange={(e) => setFormData({...formData, page_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground"
                  >
                    <option value="page">Regular Page</option>
                    <option value="blog">Blog Post</option>
                  </select>
                </div>
              </div>

              {/* Blog Settings - Only show when page_type is 'blog' */}
              {formData.page_type === 'blog' && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-500" />
                    Blog Settings
                  </h4>
                  <div>
                    <Label className="text-foreground">Cover Image URL *</Label>
                    <Input
                      value={formData.blog_cover_image}
                      onChange={(e) => setFormData({...formData, blog_cover_image: e.target.value})}
                      placeholder="/api/static/uploads/image.jpg or https://..."
                      className="bg-muted/30 border-border mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Upload image in Media Library, then paste the URL here</p>
                  </div>
                  <div>
                    <Label className="text-foreground">Excerpt</Label>
                    <textarea
                      value={formData.blog_excerpt}
                      onChange={(e) => setFormData({...formData, blog_excerpt: e.target.value})}
                      placeholder="Brief description shown on blog listing..."
                      rows={2}
                      className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-foreground">Category</Label>
                      <Input
                        value={formData.blog_category}
                        onChange={(e) => setFormData({...formData, blog_category: e.target.value})}
                        placeholder="Airport Transfer"
                        className="bg-muted/30 border-border mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-foreground">Author</Label>
                      <Input
                        value={formData.blog_author}
                        onChange={(e) => setFormData({...formData, blog_author: e.target.value})}
                        placeholder="GoTurkeyTransfer"
                        className="bg-muted/30 border-border mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-foreground">Read Time</Label>
                      <Input
                        value={formData.blog_read_time}
                        onChange={(e) => setFormData({...formData, blog_read_time: e.target.value})}
                        placeholder="5 min read"
                        className="bg-muted/30 border-border mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-foreground">Tags (comma separated)</Label>
                    <Input
                      value={formData.blog_tags}
                      onChange={(e) => setFormData({...formData, blog_tags: e.target.value})}
                      placeholder="istanbul, airport transfer, vip"
                      className="bg-muted/30 border-border mt-1"
                    />
                  </div>
                </div>
              )}

              {/* SEO */}
              <div className="p-4 bg-muted/20 rounded-lg space-y-4">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Globe className="h-4 w-4 text-primary" />
                  SEO Settings
                </h4>
                <div>
                  <Label className="text-foreground">SEO Title</Label>
                  <Input
                    value={formData.seo_title}
                    onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                    placeholder="Page title"
                    className="bg-muted/30 border-border mt-1"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Meta Description</Label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                    placeholder="Page description..."
                    rows={2}
                    className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Canonical URL</Label>
                    <Input
                      value={formData.canonical_url}
                      onChange={(e) => setFormData({...formData, canonical_url: e.target.value})}
                      placeholder="https://..."
                      className="bg-muted/30 border-border mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Robots</Label>
                    <Input
                      value={formData.robots_meta}
                      onChange={(e) => setFormData({...formData, robots_meta: e.target.value})}
                      placeholder="index, follow"
                      className="bg-muted/30 border-border mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* OG Tags */}
              <div className="p-4 bg-muted/20 rounded-lg space-y-4">
                <h4 className="font-semibold text-foreground">Open Graph (Facebook)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">OG Title</Label>
                    <Input
                      value={formData.og_title}
                      onChange={(e) => setFormData({...formData, og_title: e.target.value})}
                      className="bg-muted/30 border-border mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">OG Image URL</Label>
                    <Input
                      value={formData.og_image}
                      onChange={(e) => setFormData({...formData, og_image: e.target.value})}
                      className="bg-muted/30 border-border mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">OG Description</Label>
                  <textarea
                    value={formData.og_description}
                    onChange={(e) => setFormData({...formData, og_description: e.target.value})}
                    rows={2}
                    className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground resize-none"
                  />
                </div>
              </div>

              {/* Page Title */}
              <div>
                <Label className="text-foreground">Page Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Privacy Policy"
                  className="bg-muted/30 border-border mt-1"
                  data-testid="page-title-input"
                />
              </div>

              {/* HTML Content */}
              <div>
                <Label className="text-foreground">HTML Content</Label>
                <p className="text-xs text-muted-foreground mb-2">HTML ve CSS içerikleri desteklenir. Örn: &lt;h1&gt;, &lt;p&gt;, &lt;ul&gt;, style attributeleri</p>
                <textarea
                  value={formData.content_html}
                  onChange={(e) => setFormData({...formData, content_html: e.target.value})}
                  rows={15}
                  placeholder="<h1>Page Title</h1>
<p>Your content here...</p>
<ul>
  <li>List item 1</li>
  <li>List item 2</li>
</ul>"
                  className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground font-mono text-sm resize-y"
                  data-testid="html-content-input"
                />
              </div>

              {/* JSON-LD */}
              <div>
                <Label className="text-foreground">JSON-LD Schema</Label>
                <textarea
                  value={formData.json_ld}
                  onChange={(e) => setFormData({...formData, json_ld: e.target.value})}
                  rows={4}
                  placeholder='{"@context": "https://schema.org", ...}'
                  className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground font-mono text-sm resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving} data-testid="save-page-btn">
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminCmsPage;
